import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Mail, Edit3, Eye, Save, X, ArrowLeft, Code, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewText, setPreviewText] = useState(null);
  const [previewMode, setPreviewMode] = useState("html");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get("/admin/email-templates");
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  const openTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setEditSubject(tmpl.subject);
    setEditBody(tmpl.html_body);
    setEditMode(false);
    setPreviewHtml(null);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const response = await authAxios.put(`/admin/email-templates/${selectedTemplate.slug}`, {
        subject: editSubject,
        html_body: editBody,
      });
      setSelectedTemplate(response.data);
      setEditMode(false);
      toast.success("Template saved");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedTemplate) return;
    try {
      const response = await authAxios.post(`/admin/email-templates/${selectedTemplate.slug}/preview`);
      setPreviewHtml(response.data.html);
      setPreviewText(response.data.text);
      setPreviewMode("html");
    } catch (error) {
      toast.error("Failed to generate preview");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  // Detail/Edit view
  if (selectedTemplate) {
    return (
      <div className="space-y-6" data-testid="email-template-detail">
        <div className="flex items-center justify-between">
          <button
            onClick={() => { setSelectedTemplate(null); setPreviewHtml(null); }}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="template-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to templates
          </button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handlePreview}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="template-preview-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditSubject(selectedTemplate.subject);
                    setEditBody(selectedTemplate.html_body);
                    setEditMode(false);
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-divine-gold hover:bg-divine-gold/90 text-black"
                  data-testid="template-save-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? "Saving..." : "Save"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setEditMode(true)}
                className="bg-divine-gold hover:bg-divine-gold/90 text-black"
                data-testid="template-edit-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Template Info */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-divine-gold" />
            <div>
              <h3 className="text-white font-semibold">{selectedTemplate.name}</h3>
              <p className="text-white/40 text-xs font-mono">{selectedTemplate.slug}</p>
            </div>
          </div>
          {selectedTemplate.description && (
            <p className="text-white/50 text-sm mb-4">{selectedTemplate.description}</p>
          )}

          {/* Variables Reference */}
          <div className="bg-black/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Code className="w-4 h-4 text-divine-gold" />
              <span className="text-white/70 text-sm font-semibold">Available Variables</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(selectedTemplate.variables || []).map((v) => (
                <span
                  key={v}
                  className="text-xs font-mono bg-divine-gold/10 text-divine-gold border border-divine-gold/20 px-2 py-1 rounded cursor-pointer hover:bg-divine-gold/20 transition-colors"
                  onClick={() => {
                    if (editMode) {
                      navigator.clipboard.writeText(`{{${v}}}`);
                      toast.success(`Copied {{${v}}} to clipboard`);
                    }
                  }}
                  title={editMode ? "Click to copy" : ""}
                >
                  {"{{" + v + "}}"}
                </span>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div className="mb-4">
            <Label className="text-white/70 text-sm">Subject Line</Label>
            {editMode ? (
              <Input
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                className="mt-1 !bg-[#111] border-white/20 text-white"
                data-testid="template-subject-input"
              />
            ) : (
              <p className="mt-1 text-white/90 text-sm bg-black/20 rounded px-3 py-2">{selectedTemplate.subject}</p>
            )}
          </div>

          {/* HTML Body */}
          <div>
            <Label className="text-white/70 text-sm">HTML Body</Label>
            {editMode ? (
              <Textarea
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                className="mt-1 !bg-[#111] border-white/20 text-white font-mono text-xs leading-relaxed"
                rows={20}
                data-testid="template-body-input"
              />
            ) : (
              <pre className="mt-1 text-white/70 text-xs bg-black/20 rounded p-4 overflow-x-auto max-h-[400px] overflow-y-auto font-mono leading-relaxed">
                {selectedTemplate.html_body}
              </pre>
            )}
          </div>

          {/* Meta */}
          <div className="flex items-center gap-4 mt-4 text-white/30 text-xs">
            <span>Last updated: {formatDate(selectedTemplate.updated_at)}</span>
            <span>By: {selectedTemplate.updated_by || "system"}</span>
          </div>
        </div>

        {/* Live Preview */}
        {previewHtml && (
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4 text-divine-gold" />
                Preview (Sample Data)
              </h4>
              <div className="flex items-center gap-2">
                <div className="flex bg-black/30 rounded-lg p-0.5">
                  <button
                    onClick={() => setPreviewMode("html")}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${previewMode === "html" ? "bg-divine-gold text-black font-semibold" : "text-white/50 hover:text-white"}`}
                    data-testid="preview-html-tab"
                  >
                    HTML
                  </button>
                  <button
                    onClick={() => setPreviewMode("text")}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${previewMode === "text" ? "bg-divine-gold text-black font-semibold" : "text-white/50 hover:text-white"}`}
                    data-testid="preview-text-tab"
                  >
                    Plain Text
                  </button>
                </div>
                <button onClick={() => { setPreviewHtml(null); setPreviewText(null); }} className="text-white/40 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {previewMode === "html" ? (
              <div className="bg-white rounded-lg overflow-hidden">
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full border-0"
                  style={{ minHeight: "500px" }}
                  sandbox=""
                  data-testid="template-preview-iframe"
                />
              </div>
            ) : (
              <pre className="bg-black/30 rounded-lg p-6 text-white/80 text-sm font-mono whitespace-pre-wrap leading-relaxed overflow-auto max-h-[500px]" data-testid="template-preview-text">
                {previewText}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6" data-testid="email-templates-list">
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-divine-gold" />
            <h3 className="text-white font-semibold text-lg">Email Templates</h3>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchTemplates}
            className="text-white/50 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <p className="text-white/50 text-sm mb-6">
          Manage system email templates with variable placeholders. Click a template to view or edit.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-divine-gold animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-white/40 text-center py-8">No email templates found.</p>
        ) : (
          <div className="space-y-3">
            {templates.map((tmpl) => (
              <div
                key={tmpl.slug}
                onClick={() => openTemplate(tmpl)}
                className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/10 hover:border-divine-gold/30 cursor-pointer transition-all hover:bg-black/30 group"
                data-testid={`template-item-${tmpl.slug}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-divine-gold/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-divine-gold" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium group-hover:text-divine-gold transition-colors">{tmpl.name}</h4>
                    <p className="text-white/40 text-xs font-mono">{tmpl.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                    {(tmpl.variables || []).slice(0, 3).map((v) => (
                      <span key={v} className="text-[10px] font-mono bg-white/5 text-white/40 px-1.5 py-0.5 rounded">
                        {v}
                      </span>
                    ))}
                    {(tmpl.variables || []).length > 3 && (
                      <span className="text-[10px] text-white/30">+{tmpl.variables.length - 3}</span>
                    )}
                  </div>
                  <span className="text-white/30 text-xs whitespace-nowrap">{formatDate(tmpl.updated_at)}</span>
                  <Edit3 className="w-4 h-4 text-white/20 group-hover:text-divine-gold transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
